// src/services/api.js (Updated with missing methods)
class ApiService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'https://taskprioritiser.com';
        this.token = localStorage.getItem('authToken');
    }

    // Set auth token
    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('authToken', token);
        } else {
            localStorage.removeItem('authToken');
        }
    }

    // Get auth headers
    getHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };

        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        return headers;
    }

    // Generic request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: this.getHeaders(),
            ...options,
        };

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }

            return await response.text();
        } catch (error) {
            console.error('API Request failed:', error);
            throw error;
        }
    }

    // Auth methods
    async register(userData) {
        return this.request('/api/users/register', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    async login(credentials) {
        const response = await this.request('/api/users/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });

        if (response.token) {
            this.setToken(response.token);
        }

        return response;
    }

    async logout() {
        this.setToken(null);
        return Promise.resolve();
    }

    async getCurrentUser() {
        return this.request('/api/users/me');
    }

    async verifyEmail(token) {
        return this.request('/api/users/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    async resendVerificationEmail() {
        return this.request('/api/users/resend-verification', {
            method: 'POST',
            body: JSON.stringify({}),
        });
    }

    async forgotPassword(email) {
        return this.request('/api/users/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async verifyResetToken(token) {
        return this.request('/api/users/verify-reset-token', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    async resetPassword(token, password) {
        return this.request('/api/users/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, password }),
        });
    }

    async updateProfile(profileData) {
        return this.request('/api/users/profile', {
            method: 'PUT',
            body: JSON.stringify(profileData),
        });
    }

    async changePassword(passwordData) {
        return this.request('/api/users/change-password', {
            method: 'PUT',
            body: JSON.stringify(passwordData),
        });
    }

    // Task methods
    async analyzeTasks(tasks) {
        return this.request('/api/analyze-tasks', {
            method: 'POST',
            body: JSON.stringify(tasks),
        });
    }

    async uploadCsv(file) {
        const formData = new FormData();
        formData.append('file', file);

        return this.request('/api/upload-csv', {
            method: 'POST',
            headers: {
                Authorization: this.token ? `Bearer ${this.token}` : undefined,
            },
            body: formData,
        });
    }

    // Admin methods
    async getUsers() {
        return this.request('/api/users/users');
    }

    async updateUserRole(userId, role) {
        return this.request(`/api/users/users/${userId}/role`, {
            method: 'PUT',
            body: JSON.stringify({ role }),
        });
    }

    async toggleUserStatus(userId, isActive) {
        return this.request(`/api/users/users/${userId}/activate`, {
            method: 'PUT',
            body: JSON.stringify({ is_active: isActive }),
        });
    }

    async deleteUser(userId) {
        return this.request(`/api/users/users/${userId}`, {
            method: 'DELETE',
        });
    }

    async getSystemSettings() {
        return this.request('/api/admin/settings');
    }

    async updateSystemSettings(settings) {
        return this.request('/api/admin/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    async getTasks() {
        console.log('🔄 API: Getting user tasks...');
        const response = await this.request('/api/tasks');
        console.log('✅ API: Tasks retrieved:', response);
        return response;
    }

    // Get task by ID
    async getTask(taskId) {
        return this.request(`/api/tasks/${taskId}`);
    }

    async updateTask(taskId, taskData) {
        console.log(`🔄 API: Updating task ${taskId}...`);
        const response = await this.request(`/api/tasks/${taskId}`, {
            method: 'PUT',
            body: JSON.stringify(taskData),
        });
        console.log('✅ API: Task updated:', response);
        return response;
    }

    // Update task status
    async updateTaskStatus(taskId, status) {
        console.log(`🔄 API: Updating task ${taskId} status to ${status}...`);
        const response = await this.request(`/api/tasks/${taskId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        });
        console.log('✅ API: Task status updated:', response);
        return response;
    }

    // Delete task
    async deleteTask(taskId) {
        return this.request(`/api/tasks/${taskId}`, {
            method: 'DELETE',
        });
    }

    // Mark task as completed
    async markTaskCompleted(taskId) {
        return this.updateTaskStatus(taskId, 'completed');
    }

    // Admin: Get all tasks
    async getAllTasks() {
        return this.request('/api/admin/tasks');
    }

    // Review methods (public - no auth required)
    async submitReview(reviewData) {
        return this.request('/api/public/reviews', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(reviewData),
        });
    }

    async getPublicReviews() {
        return this.request('/api/public/reviews', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    // Contact methods (public - no auth required)
    async submitContact(contactData) {
        return this.request('/api/public/contact', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(contactData),
        });
    }

    // Admin review methods (auth required)
    async getAllReviews() {
        return this.request('/api/admin/reviews');
    }

    async updateReview(reviewId, reviewData) {
        return this.request(`/api/admin/reviews/${reviewId}`, {
            method: 'PUT',
            body: JSON.stringify(reviewData),
        });
    }

    async updateReviewStatus(reviewId, isApproved) {
        return this.request(`/api/admin/reviews/${reviewId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ is_approved: isApproved }),
        });
    }

    async deleteReview(reviewId) {
        return this.request(`/api/admin/reviews/${reviewId}`, {
            method: 'DELETE',
        });
    }

    // Admin contact methods (auth required)
    async getContactSubmissions() {
        return this.request('/api/admin/contacts');
    }

    async updateContactStatus(contactId, status) {
        return this.request(`/api/admin/contacts/${contactId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        });
    }
}

export default new ApiService();